/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useMutation } from 'react-query';
import Tooltip from '../../Tooltip';

import TableRowAction from '../TableRowAction';
import EditableTitle from '../EditableTitle';
import MeetingThumbnail from '../MeetingThumbnail';
import NotificationManagerModal from '../../../modals/NotificationManagerModal';

// import videoIcon from '../../../assets/images/my-library/video.svg';
import playIcon from '../../../assets/images/my-meetings/play.svg';
import downloadVideo from '../../../helpers/downloadVideo';
import ActionsMenu from '../ActionsMenu';
import formatDuration from '../../../helpers/formatDuration';
import useMeetingProcessing from '../../../hooks/useMeetingProcessing';
import Progress from './Progress';
import { ORG_ROLES, USER_ROLES } from '../../../constants/main';
import newIcon from '../../../assets/images/my-library/new.svg';
import viewedIcon from '../../../assets/images/my-library/viewed.svg';
import useMeetingReady from '../../../hooks/useMeetingReady';
import usePublishFailed from '../../../hooks/usePublishFailed';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import MeetingService from '../../../services/MeetingService';
// eslint-disable-next-line import/no-named-as-default
import useAuthorization from '../../../hooks/useAuthorization';
import { useUiContext } from '../../../context/UiContext';
import classes from './styles.module.scss';

const MeetingRow = ({
  meeting,
  user,
  folderUserRole,
  handleDetails,
  handleDelete,
  handleMoveToTrash,
  handleRestore,
  handleCopyLink,
  refetchFolder,
  showReport,
  isTrashBin,
  handleManageAccess,
  handleMove,
}) => {
  const [processingProgress, setProcessingProgress] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [
    isNotificationManagerVisible,
    setIsNotificationManagerVisible,
  ] = useState(false);

  const processing = useMeetingProcessing();

  const navigate = useNavigate();

  const isMobile = useCheckMobileScreen();

  const { notifyError, notifySuccess } = useUiContext();

  // Use authorization hook for permission checks
  const resource = React.useMemo(() => ({ userId: meeting.userId }), [meeting.userId]);
  const resourceUser = React.useMemo(() => {
    const { userRole: meetingUserRole } = meeting.MeetingUsers?.[0] || {};
    // For meetings, check MeetingUser role first, fallback to folderUserRole
    if (meetingUserRole) {
      return { role: meetingUserRole };
    }
    // If no MeetingUser role, use folder role (for folder-level access)
    if (folderUserRole) {
      return { role: folderUserRole === 'owner' || folderUserRole === 'creator' ? 'creator' : folderUserRole };
    }
    return null;
  }, [meeting.MeetingUsers, folderUserRole]);

  const {
    canEditMeeting,
    canDeleteMeeting,
    canManageMeetingPermissions,
    isFolderOwner,
    isOrgOwner,
  } = useAuthorization(
    user,
    resource,
    resourceUser,
    'meeting',
    {
      folderId: meeting.folderId,
      folderUserRole,
    }
  );

  const { userRole: meetingUserRole } = meeting.MeetingUsers?.[0] || {};
  // Check if there's a published version (from MeetingVersion table) instead of just current status
  // This allows viewing even when status is 'draft' if a published version exists
  const hasPublishedVersion = meeting.hasPublishedVersion === true || meeting.hasPublishedVersion === 'true';
  const { latestPublishedVersion } = meeting;

  const isNotAllowedToView = !hasPublishedVersion;

  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

  const isNotAllowedToDelete =
    (meeting.status === 'in progress' && !isSuperAdmin) || !canDeleteMeeting;

  const isNotAllowedToDownload =
    !hasPublishedVersion || user?.orgRole?.access !== ORG_ROLES.OWNER;

  const isMeetingInProgress = meeting.status === 'in progress';
  const isMenuDisabled = isMeetingInProgress && !isSuperAdmin;

  const isNotAllowedToEdit =
    isMenuDisabled || !canEditMeeting;

  const isNotAllowedToShare = !canManageMeetingPermissions;

  const isNotAllowedToCopyLink = !canManageMeetingPermissions;

  const isNotAllowedToGetViewerReport =
    (!hasPublishedVersion || meetingUserRole === 'viewer') &&
    user?.orgRole?.access !== ORG_ROLES.OWNER;

  const duration = formatDuration(meeting.duration);

  const lastPublishedDate = moment(meeting.updatedAt).format('MMM D, YYYY');

  const { isViewed } = meeting;

  const { mutate: updateMeeting } = useMutation(MeetingService.updateMeeting, {
    onSuccess: () => {
      refetchFolder();
      setIsEditingTitle(false);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update video';
      notifyError(errorMessage);
      setIsEditingTitle(false);
    },
  });

  const title = (
    <EditableTitle
      handleUpdateTitle={(newTitle) =>
        updateMeeting({ meetingId: meeting.id, title: newTitle })
      }
      isEditable={!isNotAllowedToEdit}
      isEditing={isEditingTitle}
      setIsEditing={setIsEditingTitle}
    >
      {meeting.title}
    </EditableTitle>
  );

  // Refech folder when video processing is finished
  useEffect(() => {
    if (processing.step === 'finishing') {
      refetchFolder();
    }
  }, [processing, refetchFolder]);

  // Set processing progress, filter by meeting id
  useEffect(() => {
    if (processing.meetingId === meeting.id) {
      setProcessingProgress(processing);
    }
  }, [meeting.id, processing]);

  useMeetingReady((meetingId) => {
    refetchFolder();
    // Show success toast if this meeting finished publishing
    if (meetingId === meeting.id) {
      setProcessingProgress(null);
      notifySuccess(`"${meeting.title}" has been published successfully.`);
    }
  });

  usePublishFailed((meetingId) => {
    refetchFolder();
    // Clear progress state if this meeting failed
    if (meetingId === meeting.id) {
      setProcessingProgress(null);
    }
    notifyError('Publishing failed. Your video has been reverted to draft.');
  });

  return (
    <tr key={meeting.id} className={classes.MeetingRow} data-testid={`meeting-row-${meeting.id}`}>
      <td className={classes.title}>
        <div className={classes.innerContainer}>
          <div className={classes.imagesContainer}>
            {!isViewed && (
              <img src={newIcon} alt="New" className={classes.newIcon} />
            )}
            {/* <div className={classes.icon}>
              <img
                src={meeting.signedThumbnailS3FileKey || videoIcon}
                alt="Video"
              />
            </div> */}
            <MeetingThumbnail meeting={meeting} />
          </div>
          <div className={classes.infoContainer}>
            {title}
            {meeting.status !== 'in progress' ? (
              <span className={classes.publishedDate}>
                {isMobile ? '' : 'modified '}
                {lastPublishedDate}
              </span>
            ) : (
              <Progress
                isVisible
                percentage={processingProgress?.percentage || 0}
                step={processingProgress?.step || 'Publishing'}
              />
            )}
          </div>
        </div>
        <button
          className={classes.playButton}
          disabled={isNotAllowedToView}
          type="button"
          onClick={() => navigate(`/my-videos/${meeting.id}`)}
        >
          <img src={playIcon} alt="Play" />
        </button>
      </td>
      <td className={classes.owner}>
        {meeting.userId === user?.id ? 'Me' : meeting.Owner.name}
      </td>
      {!isTrashBin && (
        <td className={classes.owner}>
          {meeting.publishedByName || '-'}
        </td>
      )}
      <td className={classes.publishedDate}>
        {processingProgress && processingProgress.step !== 'finishing'
          ? 'Processing'
          : lastPublishedDate}
      </td>
      <td className={classes.buttons}>
        <div className={classes.buttonsContainer}>
          <span className={classes.duration}>
            {meeting.duration && duration}
          </span>
          {isNotAllowedToView ? (
            <Tooltip
              position="left"
              text={
                canEditMeeting
                  ? "This video isn't published yet. Click the pencil icon to edit, then select Publish to make it visible."
                  : "This video isn't published yet."
              }
              width={200}
            >
              <button
                disabled={isNotAllowedToView}
                type="button"
                onClick={() => navigate(`/my-videos/${meeting.id}`)}
              >
                <img src={playIcon} alt="Play" />
              </button>
            </Tooltip>
          ) : (
            <div className={classes.viewButtonContainer}>
              <button
                className={classes.playButton}
                disabled={isNotAllowedToView}
                type="button"
                onClick={() => navigate(`/my-videos/${meeting.id}`)}
              >
                <img src={playIcon} alt="Play" />
              </button>
              {latestPublishedVersion != null && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    top: 35,
                    fontSize: '14px',
                    color: '#666',
                    fontWeight: 500,
                  }}
                >
                  v{Number(latestPublishedVersion)}
                </span>
              )}
              {isViewed && (
                <img
                  src={viewedIcon}
                  alt="Viewed"
                  className={classes.viewedButtonIcon}
                />
              )}
            </div>
          )}
        </div>
      </td>
      {!isMobile && (
        <td>
          <TableRowAction
            handleDelete={isTrashBin ? handleDelete : handleMoveToTrash}
            noEdit={isTrashBin || isMobile}
            handleEdit={() => {
              navigate(`/my-library/${meeting.id}/edit`);
            }}
            handleManageAccess={handleManageAccess ? () => handleManageAccess(meeting) : undefined}
            noManageAccess={isTrashBin}
            canEdit={canEditMeeting}
            canDelete={canDeleteMeeting}
            canManageAccess={canManageMeetingPermissions || (resourceUser?.role === 'contributor')}
            meetingId={meeting.id}
            disabled={isMenuDisabled}
            disabledTooltip="This video is currently being published."
          />
        </td>
      )}

      <td className={classes.actionMenu}>
        <ActionsMenu
          data={meeting}
          handleDetails={handleDetails}
          handleDelete={handleDelete}
          handleMoveToTrash={handleMoveToTrash}
          handleRestore={handleRestore}
          handleEdit={() => {
            // navigate(`/my-videos/${meeting.id}/edit`)
            setIsEditingTitle(true);
          }}
          handleShare={() =>
            navigate(`/my-videos/${meeting.id}/edit?share=true`)
          }
          handleDownload={() => downloadVideo(meeting)}
          // handleGetViewerReport={() =>
          //   window.open(`/viewer-report/${meeting.id}`, '_blank')
          // }
          handleGetViewerReport={() => showReport(meeting)}
          handleCopyLink={handleCopyLink}
          handleMove={handleMove && !isTrashBin && (isOrgOwner || isFolderOwner) ? () => handleMove(meeting) : undefined}
          isNotAllowedToDelete={isNotAllowedToDelete}
          isNotAllowedToEdit={isNotAllowedToEdit}
          isNotAllowedToShare={isNotAllowedToShare}
          isNotAllowedToDownload={isNotAllowedToDownload}
          isNotAllowedToCheckDetails={false}
          isNotAllowedToGetViewerReport={isNotAllowedToGetViewerReport}
          isNotAllowedToCopyLink={isNotAllowedToCopyLink}
          isTrashBin={isTrashBin}
          isNotAllowedToSetReminder={isNotAllowedToEdit}
          disabled={isMenuDisabled}
          disabledTooltip="This video is currently being published."
          handleSetReminder={() => {
            setIsNotificationManagerVisible(true);
          }}
        />
      </td>
      <NotificationManagerModal
        show={isNotificationManagerVisible}
        handleClose={() => setIsNotificationManagerVisible(false)}
        reminder={meeting?.EmailReminder}
        meetingId={meeting?.id}
        refetch={refetchFolder}
      />
    </tr>
  );
};

export default MeetingRow;
